import { COLORS_BY_ID } from '../data/colors'
import type { ColorId } from '../data/colors'
import { ColorCardImage } from './ColorCardImage'
import { useT } from '../../../i18n'

export interface RecapGroup {
  color: ColorId
  /** Display names of everyone who picked this color. */
  names: string[]
  /** Sole majority color for the round (ties highlight nothing, same as the stepped reveal). */
  isMajority: boolean
}

export interface RecapRound {
  /** 0-indexed round number. */
  index: number
  theme: string
  subject: string
  unanimous: boolean
  /** Sorted by group size, largest first. */
  groups: RecapGroup[]
}

/**
 * The whole match on one screen: every round's subject and who picked
 * what color. Rendered under the final scoreboard so the end screen
 * tells the story of the game, not just the score. Shared by the
 * online room results and pass play; the TV finale draws its own
 * big-screen variant of the same data.
 */
export function AllRoundsRecap({ rounds }: { rounds: RecapRound[] }) {
  const t = useT()
  if (rounds.length === 0) return null
  return (
    <div>
      <h3 className="font-semibold text-brand-text mb-3">{t('Every round')}</h3>
      <div className="space-y-3">
        {rounds.map(r => (
          <div key={r.index} className="card p-4">
            <p className="text-xs font-semibold text-brand-muted uppercase tracking-wide">
              {t('Round {n}', { n: r.index + 1 })} · {r.theme}
            </p>
            <p className="font-display text-lg font-bold text-brand-text mt-0.5 leading-snug">
              {r.subject}
            </p>
            {r.unanimous && (
              <p className="text-xs text-iet-green font-semibold mt-1">{t('✨ Unanimous!')}</p>
            )}
            <div className="mt-3 space-y-1.5">
              {r.groups.map(g => (
                <div
                  key={g.color}
                  className={`flex items-center gap-2.5 rounded-xl px-2 py-1.5
                              ${g.isMajority ? 'bg-brand-gold/10 border border-brand-gold/40' : ''}`}
                >
                  <ColorCardImage color={g.color} size="sm" className="shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-brand-text">
                      {COLORS_BY_ID[g.color]?.name ?? g.color}
                    </p>
                    <p className="text-xs text-brand-muted">{g.names.join(', ')}</p>
                  </div>
                  <span className="font-display font-bold text-brand-text shrink-0">
                    ×{g.names.length}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
